import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { billingApi } from '@/services/api/billingApi';
import { extractError } from '@/lib/extractError';
import { isSubscriptionActive } from '@/lib/subscription';
import type { PlanCard, SubscriptionState, UsageSummary } from '@/types';

interface PlanState {
  catalog: PlanCard[];
  subscription: SubscriptionState | null;
  usage: UsageSummary | null;
  loading: boolean;
  error: string | null;
  /** Set once the first hydrate resolves, so the UI can distinguish
   *  "never loaded" from "loaded but empty". */
  hydrated: boolean;
  /** True when the most recent `GET /billing/subscription` read FAILED
   *  (network / 5xx / timeout) — as opposed to succeeding with an inactive
   *  status. Lets the gate fail OPEN on a transient error instead of locking
   *  an entitled user behind the paywall. */
  subscriptionError: boolean;
  /** Set when the BE returns `402 NO_ACTIVE_PLAN` on any gated endpoint — an
   *  authoritative "this user has no active plan" signal. Overrides the
   *  fail-open branch in `SubscriptionGate`: a 402 is proof of no entitlement
   *  even if a later `GET /billing/subscription` read fails. Cleared by a
   *  successful read of an active subscription. */
  subscriptionRequired: boolean;
}

interface PollOptions {
  attempts?: number;
  intervalMs?: number;
  /** Checked after every read; true stops the poll (e.g. the caller unmounted). */
  isCancelled?: () => boolean;
}

interface PlanActions {
  /** Load catalog + subscription + usage in parallel (LIL-335 AC). */
  hydrate: () => Promise<void>;
  /** Mark the user as having no active plan, in response to a BE
   *  `402 NO_ACTIVE_PLAN`. Flips `SubscriptionGate` to the Paywall. */
  flagSubscriptionRequired: () => void;
  /** Re-fetch only usage (e.g. after an action consumes credits). */
  refreshUsage: () => Promise<void>;
  /** Re-hydrate until `predicate` holds for the subscription (or the attempts
   *  run out). Resolves whether it held on the last read. */
  pollUntil: (
    predicate: (subscription: SubscriptionState | null) => boolean,
    opts?: PollOptions
  ) => Promise<boolean>;
  /** After a checkout return, the BE webhook may not have flipped the
   *  subscription yet. Resolves `true` once it reads active. */
  pollUntilActive: (opts?: PollOptions) => Promise<boolean>;
  clearError: () => void;
  /** Forget everything read for the signed-in user; the next screen re-hydrates. */
  reset: () => void;
}

const INITIAL_PLAN_STATE: PlanState = {
  catalog: [],
  subscription: null,
  usage: null,
  loading: false,
  error: null,
  hydrated: false,
  subscriptionError: false,
  subscriptionRequired: false,
};

export const usePlanStore = create<PlanState & PlanActions>()(
  devtools(
    (set, get) => ({
      ...INITIAL_PLAN_STATE,

      hydrate: async () => {
        set({ loading: true, error: null }, false, 'plan/hydrate');

        // allSettled (not all) so one failing read doesn't discard the others
        // — a catalog 500 must not wipe a good subscription read, and vice
        // versa. Each result is applied independently below.
        const [catalogRes, subRes, usageRes] = await Promise.allSettled([
          billingApi.getCatalog(),
          billingApi.getSubscription(),
          billingApi.getUsage(),
        ]);

        const next: Partial<PlanState> = { loading: false, hydrated: true };

        if (catalogRes.status === 'fulfilled') {
          next.catalog = catalogRes.value.plans ?? [];
        }

        if (subRes.status === 'fulfilled') {
          next.subscription = subRes.value;
          next.subscriptionError = false;
          // A fresh, successful read of an active subscription clears the
          // `NO_ACTIVE_PLAN` flag (e.g. after the user completes checkout). An
          // inactive read leaves the flag untouched — the gate locks via
          // `isSubscriptionActive` anyway.
          if (isSubscriptionActive(subRes.value)) {
            next.subscriptionRequired = false;
          }
        } else {
          // Keep the last-known subscription and flag the read as failed.
          // A successful read of `none`/`canceled` is NOT an error and still
          // gates the user — only a genuine read failure sets this so the gate
          // can fail open. See SubscriptionGate.
          next.subscriptionError = true;
        }

        if (usageRes.status === 'fulfilled') {
          next.usage = usageRes.value;
        }

        const firstRejected = [catalogRes, subRes, usageRes].find(
          (r): r is PromiseRejectedResult => r.status === 'rejected'
        );
        next.error = firstRejected ? extractError(firstRejected.reason) : null;

        set(next, false, firstRejected ? 'plan/hydrate/partial' : 'plan/hydrate/success');
      },

      flagSubscriptionRequired: () =>
        set({ subscriptionRequired: true }, false, 'plan/flagSubscriptionRequired'),

      refreshUsage: async () => {
        try {
          const usage = await billingApi.getUsage();
          set({ usage }, false, 'plan/refreshUsage/success');
        } catch (err) {
          set({ error: extractError(err) }, false, 'plan/refreshUsage/error');
        }
      },

      pollUntil: async (predicate, opts) => {
        // Stripe's `checkout.session.completed` webhook routinely lags the
        // browser redirect by 10–30s (longer under load), so the window has to
        // outlast that — a short poll gives up while the payment is still
        // settling and strands a paying user on the paywall. ~15 attempts ×
        // 3s ≈ 45s of polling before we surface a recoverable timeout.
        const attempts = opts?.attempts ?? 15;
        const intervalMs = opts?.intervalMs ?? 3000;
        for (let i = 0; i < attempts; i++) {
          await get().hydrate();
          if (predicate(get().subscription)) return true;
          if (opts?.isCancelled?.()) return false;
          if (i < attempts - 1) {
            await new Promise(resolve => window.setTimeout(resolve, intervalMs));
            if (opts?.isCancelled?.()) return false;
          }
        }
        return predicate(get().subscription);
      },

      pollUntilActive: opts => get().pollUntil(isSubscriptionActive, opts),

      clearError: () => set({ error: null }, false, 'plan/clearError'),

      reset: () => set(INITIAL_PLAN_STATE, false, 'plan/reset'),
    }),
    {
      name: 'PlanStore',
      enabled: import.meta.env.DEV,
    }
  )
);
